import React, { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  AccountOutline as AlunoIcon,
  AccountCheck as AlunoCheckIcon,
} from "mdi-material-ui";
import {
  EAcaoLogUser,
  EGrupoAcaoLogUser,
  ELogUserStatus,
  IAlunoRecuperacao,
  IAlunosChamada,
  IPeriodoEtapa,
} from "@deltasge/marauders-map";
import { api } from "configs";
import { gravaLogUser } from "configs/log";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { Avatar } from "components/Avatar";
import { LoadingButton } from "@mui/lab";

export const AlunosRecuperacao: FC<{
  idDiaAula: string;
  recEtapa: IPeriodoEtapa;
  onClose: (reload?: boolean) => void;
}> = ({ idDiaAula, recEtapa, onClose }) => {
  const [alunos, setAlunos] = useState<IAlunosChamada[]>([]);
  const [selecionados, setSelecionados] = useState<IAlunosChamada[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const controller = new AbortController();

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        setLoading(true);
        const { data: alunos } = await api.get<IAlunosChamada[] | undefined>(
          `/educador/chamada/${idDiaAula}`,
          {
            params: {
              etapaRecuperacao: recEtapa.etapa,
            },
            signal: controller.signal,
            cache: { ignoreCache: true },
          }
        );
        if (alunos && ativo) {
          setAlunos(alunos);
          setSelecionados(alunos.filter((f) => f.recuperacao));
        }
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      ativo = false;
      controller.abort();
    };
  }, [idDiaAula]);

  const handleSubmit = async () => {
    try {
      setSalvando(true);
      const deleted = alunos
        .filter(
          (aluno) =>
            !selecionados.map((m) => m.idMatricula).includes(aluno.idMatricula)
        )
        .filter((f) => f.idNota != undefined);

      await api.delete(`falta`, {
        params: {
          where: {
            "idMatricula.in": deleted.map((m) => m.idMatricula).join(","),
            diaAula: {
              isRecuperacao: true,
              "data.between": [
                recEtapa.dataRecuperacaoIni,
                recEtapa.dataRecuperacaoFim,
              ],
            },
          },
        },
      });
      await api.delete(
        `aluno-recuperacao/${deleted.map((m) => m.idNota).join(",")}`
      );
      gravaLogUser({
        logKey: {
          acao: EAcaoLogUser.Exclusao,
          grupoAcao: EGrupoAcaoLogUser.Aluno,
        },
        status: ELogUserStatus.Aguardando,
        message: `excluiu ${deleted.length} registro(s) de recuperação`,
        dataBefore: { deleted: deleted.map((m) => m.idNota) },
      });
      const payload = selecionados.map((m) => {
        if (!m.idNota) {
          throw new Error("Aluno sem nota associada para recuperação.");
        }
        const rec: Partial<IAlunoRecuperacao> = {
          idNota: m.idNota,
          [`r${recEtapa.etapa}`]: true,
        };
        return rec;
      });
      await api.post("aluno-recuperacao", payload);
      gravaLogUser({
        logKey: {
          acao: EAcaoLogUser.Inclusao,
          grupoAcao: EGrupoAcaoLogUser.Aluno,
        },
        status: ELogUserStatus.Aguardando,
        message: `incluiu ${payload.length} aluno(s) em recuperação`,
        dataAfter: { alunos: payload },
      });
      onClose(true);
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Box sx={{ height: "100%", overflow: "auto", width: "100%" }}>
        <DialogContent dividers style={{ paddingBottom: 0 }}>
          {loading && <LinearProgress />}
          {!loading && alunos.length > 0 && (
            <List>
              {alunos.map((aluno, index) => {
                const checked =
                  selecionados.find((f) => f.alunoUid == aluno.alunoUid) !=
                  undefined;
                return (
                  <ListItemButton
                    key={`aluno-rec-${index}`}
                    onClick={() => {
                      if (checked) {
                        setSelecionados((p) =>
                          p.filter((f) => f.alunoUid != aluno.alunoUid)
                        );
                      } else {
                        setSelecionados((p) => p.concat(aluno));
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        color="secondary"
                        icon={<AlunoIcon />}
                        checkedIcon={<AlunoCheckIcon />}
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemAvatar>
                      <Avatar
                        key={"avatar_" + aluno.photoUrl}
                        displayName={aluno.alunoNome}
                        src={aluno.photoUrl}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${
                        aluno.nChamada
                      } - ${aluno.alunoNome.rUpper()}`}
                      secondary={aluno.nota ? `Nota: ${aluno.nota}` : undefined}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Box>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={() => onClose()}>
          Fechar
        </Button>
        <LoadingButton
          loading={salvando}
          variant="text"
          disabled={selecionados.length == 0}
          onClick={handleSubmit}
        >
          Salvar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
